/**
 * useJobArrivalChime — plays an escalating chime when new jobs arrive on
 * the driver's queue.
 *
 * Rule (per user spec): "1 ding for 1st job, 2 for 2nd, 3 for 3rd, etc".
 * i.e. when the driver's queue count JUMPS UP, play `newCount` chimes so the
 * driver hears the queue backing up (louder = more urgent).
 *
 * Implementation:
 *   - Web Audio API for synthesized tones — no bundled audio file needed and
 *     works offline (drivers on flaky mobile data).
 *   - Two-tone chime (A5 + C#6, a major 3rd) for a pleasant "ping" quality.
 *   - Browser autoplay policy: AudioContext starts suspended until the user
 *     interacts with the page. We auto-resume on the first tap/click anywhere.
 *   - Persist an "audio unlocked" flag in localStorage so we don't have to
 *     re-prompt after every page load.
 *
 * Usage:
 *   const { armed, needsUnlock, unlock, playChime } = useJobArrivalChime(jobs.length);
 *   {needsUnlock && <button onClick={unlock}>Tap to enable job alerts</button>}
 */
import { useEffect, useRef, useState, useCallback } from "react";

const AUDIO_UNLOCKED_KEY = "nts_driver_audio_unlocked";

// Chime spec (deliberately not too "phone ring" — we want an insistent but
// pleasant alert that survives being one-among-many on a truck cab)
const CHIME_FREQS = [880, 1108.73]; // A5 + C#6 (major third)
const CHIME_DURATION_MS = 220;
const CHIME_GAP_MS = 380;           // gap between successive dings in a burst

export function useJobArrivalChime(currentJobCount) {
  // Persistent AudioContext — one per hook instance / page lifetime.
  const ctxRef = useRef(null);
  const prevCountRef = useRef(null); // null = first render (don't ding on load)

  // "armed" = we have an AudioContext AND it's not suspended (user has interacted).
  // "needsUnlock" surfaces the unlock CTA in the UI.
  const [armed, setArmed] = useState(false);
  const [needsUnlock, setNeedsUnlock] = useState(false);

  // Create the AudioContext lazily. Some browsers create it in "suspended"
  // state until the user interacts — we can still create the node graph,
  // it just won't produce sound until `.resume()`.
  const ensureContext = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
      return ctxRef.current;
    } catch {
      return null;
    }
  }, []);

  const playSingleChime = useCallback(() => {
    const ctx = ensureContext();
    if (!ctx || ctx.state === "suspended") return;
    const now = ctx.currentTime;
    // Master gain for the whole chime — envelope shape gives us a soft
    // ping (fast attack, quick decay) rather than a harsh square burst.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(0.28, now + 0.015);      // attack
    master.gain.exponentialRampToValueAtTime(0.0001, now + CHIME_DURATION_MS / 1000);
    master.connect(ctx.destination);

    // Two oscillators for the major-third dyad.
    for (const freq of CHIME_FREQS) {
      const osc = ctx.createOscillator();
      osc.type = "triangle"; // softer than square, more "bell"-like than sine
      osc.frequency.setValueAtTime(freq, now);
      osc.connect(master);
      osc.start(now);
      osc.stop(now + CHIME_DURATION_MS / 1000);
    }
  }, [ensureContext]);

  // Play `count` chimes back-to-back with CHIME_GAP_MS spacing.
  const playChime = useCallback((count = 1) => {
    const n = Math.max(1, Math.min(count, 12)); // safety clamp — never spam >12
    for (let i = 0; i < n; i++) {
      setTimeout(() => playSingleChime(), i * CHIME_GAP_MS);
    }
  }, [playSingleChime]);

  // Unlock — call from a user-gesture handler (click/tap). Resumes the
  // AudioContext and persists the "unlocked" state so we don't nag next load.
  const unlock = useCallback(async () => {
    const ctx = ensureContext();
    if (!ctx) return;
    try {
      await ctx.resume();
      // Small silent nudge to fully "arm" iOS's WebAudio (some Safari
      // versions won't produce sound until an actual oscillator has fired
      // inside the same user gesture).
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      g.connect(ctx.destination);
      const o = ctx.createOscillator();
      o.frequency.value = 440;
      o.connect(g);
      o.start();
      o.stop(ctx.currentTime + 0.02);
      localStorage.setItem(AUDIO_UNLOCKED_KEY, "1");
      setArmed(true);
      setNeedsUnlock(false);
    } catch {
      // If resume() fails (rare — usually permissions), leave the CTA visible.
    }
  }, [ensureContext]);

  // On mount: attempt to arm silently. If context comes up "running", we
  // don't need to nag the driver. If it's "suspended" AND we've previously
  // been unlocked (localStorage flag), try to resume — some browsers keep
  // the permission after a reload.
  useEffect(() => {
    const previouslyUnlocked = localStorage.getItem(AUDIO_UNLOCKED_KEY) === "1";
    const ctx = ensureContext();
    if (!ctx) {
      setNeedsUnlock(false); // can't do anything, hide the CTA
      return;
    }
    if (ctx.state === "running") {
      setArmed(true);
      setNeedsUnlock(false);
      return;
    }
    if (previouslyUnlocked) {
      ctx.resume().then(() => {
        if (ctx.state === "running") {
          setArmed(true);
          setNeedsUnlock(false);
        } else {
          setNeedsUnlock(true);
        }
      }).catch(() => setNeedsUnlock(true));
    } else {
      setNeedsUnlock(true);
    }
  }, [ensureContext]);

  // Fallback auto-unlock: attach a one-shot listener on the document that
  // auto-resumes the context on the driver's first tap anywhere on the page.
  // This is a graceful "just works" path for drivers who don't notice the
  // "Enable alerts" button.
  useEffect(() => {
    if (armed) return;
    const handler = () => {
      unlock();
    };
    document.addEventListener("pointerdown", handler, { once: true, passive: true });
    document.addEventListener("keydown", handler, { once: true });
    return () => {
      document.removeEventListener("pointerdown", handler);
      document.removeEventListener("keydown", handler);
    };
  }, [armed, unlock]);

  // Fire the chime when the queue count JUMPS UP. `currentJobCount` is the
  // driver's active-job queue length. On the very first render we just seed
  // prevCountRef and stay silent — no chime on page load.
  useEffect(() => {
    if (prevCountRef.current === null) {
      prevCountRef.current = currentJobCount;
      return;
    }
    if (currentJobCount > prevCountRef.current) {
      // Play `currentJobCount` chimes — escalates with queue depth so the
      // driver hears their backlog growing.
      playChime(currentJobCount);
    }
    prevCountRef.current = currentJobCount;
  }, [currentJobCount, playChime]);

  return { armed, needsUnlock, unlock, playChime };
}
